/**
 * Le module **Conformité LBC/FT** — la surveillance des transactions, enfin
 * atteignable (epic 11, `EX-AML-004` à `EX-AML-011`).
 *
 * Maquette : artboard `b_conformite` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/compliance-maquette.test.tsx` **relit** cette maquette, le contrat
 * Java et la migration, et les confronte au livré, plutôt que d'en recopier les
 * valeurs ici : un attendu dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce qui manquait, et ce n'était pas un écran de plus
 *
 * `SurveillanceApi` sert **sept** routes — la file, l'investigation, les règles,
 * les rejeux, le reporting, le dossier de soupçon et son export. Toutes
 * éprouvées, toutes vertes, **aucune appelée par un écran**. C'est la dixième
 * occurrence de la famille de `D-25`, `D-30`, `D-35`, `D-45` et `D-57`, et le
 * module rendait encore l'écran provisoire pendant que le moteur levait de
 * vraies alertes en base. Les sept sont appelées ici.
 *
 * ## Ce que la file NE fait pas, et c'est le point le plus important
 *
 * **Elle ne résout aucune identité.** La maquette écrit « 77 645 12 08 » dans
 * sa colonne « Compte » ; la route rend un `customerId`, et rien d'autre. Il
 * aurait suffi d'un appel par ligne à `GET /customers/{id}` pour afficher des
 * noms — et la file aurait alors révélé cent états civils en écrivant au
 * journal la ligne qu'elle écrit aujourd'hui, `AML_ALERT_QUEUE` avec le seul
 * nombre d'alertes. C'est **exactement** la faute que la file de revue KYC a
 * payée hier, corrigée en passant son entrée de `KYC_QUEUE` à
 * `KYC_QUEUE_IDENTITIES` : une lecture qui montre deux cents états civils et
 * une lecture qui ne montre que des identifiants n'écrivaient pas deux lignes
 * différentes.
 *
 * L'identité n'apparaît donc qu'à l'**investigation**, appelée à l'ouverture
 * d'une ligne — un acte, journalisé un par un, et dont l'entrée dit en plus
 * **combien de contreparties ont été révélées**, parce que le graphe expose des
 * tiers qui n'ont rien demandé (`EX-BO-004`, `EX-DAT-007`). Et encore : ce que
 * le serveur rend alors est une **référence publique de compte**, jamais un
 * état civil.
 *
 * Un cas garde cette conduite, et une mutation du catalogue la casse.
 *
 * ## `EX-AML-008` — la prohibition qui prime sur le dessin
 *
 * « Sur un dossier de déclaration de soupçon, le système ne génère **aucune**
 * notification client. L'interdiction d'informer est absolue : elle prime sur
 * toute règle de notification, y compris la notification écrite
 * inconditionnelle d'`EX-NOT-001`. »
 *
 * Le dessin n'offre aucune affordance de ce genre — ses trois commandes sont
 * « Classer sans suite », « Escalader au responsable », « Déclaration de
 * soupçon » — et il n'y a donc **aucune divergence à déclarer de ce côté**.
 * Ce qui est tenu ici est l'autre moitié : la section du dossier de soupçon ne
 * porte **aucun contrôle** en dehors de son export, et le panneau le **dit**.
 * Un cas vérifie la prohibition par sa forme la plus dure — l'ensemble des
 * commandes atteignables sur ce chemin — plutôt que par l'absence d'un libellé
 * qu'il suffirait de renommer pour la contourner.
 *
 * ## Le graphe des contreparties : la décision, et pourquoi
 *
 * `EX-AML-006` exige que le graphe montre « qui envoie à qui, avec montants et
 * fréquences ». **Le porteur n'a dessiné aucun graphe** : son artboard n'a
 * qu'une rangée de filtres, une table et un panneau de faits. Dessiner ici un
 * diagramme de nœuds et d'arêtes aurait inventé une mise en page que le porteur
 * n'a jamais tracée, sur l'écran le plus dense du produit.
 *
 * Le graphe est donc rendu **dans la langue du dessin** : une section du
 * panneau, une ligne par arête, libellé à gauche et valeur à droite — la
 * contrepartie et le sens d'un côté, le nombre d'opérations, le total et la
 * moyenne de l'autre. Les trois choses qu'`EX-AML-006` nomme y sont, au complet
 * et sans perte : qui envoie à qui, les montants, les fréquences. Ce que la
 * forme visuelle donnerait en plus — voir une étoile ou un cycle d'un coup
 * d'œil — est **déclaré absent** plutôt que bricolé : c'est un dessin à
 * demander au porteur, pas à improviser. Les scénarios qui repèrent ces formes
 * — `AML-07` étoile, `AML-08` circularité — les repèrent déjà côté serveur et
 * lèvent leurs propres alertes ; l'analyste ne dépend donc pas de son œil pour
 * les voir.
 *
 * ## Ce que le dessin montre et que le serveur ne sert pas
 *
 * Chaque omission a la même cause, et chacune se déclare à l'écran plutôt que
 * de se combler par une valeur plausible :
 *
 *  1. **le filtre « Période »** — la route de la file n'a ni borne haute ni
 *     borne basse : `status`, `scenario`, `assignedTo`, `minimumSeverity`, et
 *     rien d'autre. Le reporting, lui, en prend, et applique trente jours quand
 *     on ne lui en donne pas ; c'est la fenêtre que les indicateurs annoncent ;
 *  2. **« Affectée à : Moi »** — la session ne porte pas l'identifiant de
 *     l'opérateur (`LoginResponse` rend un rôle et un nom d'affichage), et le
 *     filtre prend un `UUID`. « Moi » est donc inexprimable ; la liste offre
 *     les analystes **présents dans la file**, comme le journal d'audit offre
 *     les acteurs présents dans sa fenêtre ;
 *  3. **la colonne « Compte »** — un `customerId`, jamais un numéro : voir
 *     plus haut, c'est un refus et non un manque ;
 *  4. **la colonne « Âge »** — aucune route ne mesure l'âge d'une alerte. La
 *     file de revue KYC sert un `ageSeconds` **mesuré par le serveur** ; celle-ci
 *     ne sert qu'un instant. Le soustraire à l'horloge du poste ferait d'une
 *     file fraîche une file en retard sur un poste mal réglé — et c'est l'écran
 *     où l'on décide quelle alerte a trop attendu. La colonne montre donc
 *     l'instant de levée, mis en forme dans la langue de l'agent ;
 *  5. **l'indicateur « > 24 h »** — même cause, même conduite : le tiret de
 *     l'absence de mesure, et la phrase qui le dit ;
 *  6. **l'indicateur « Faux positifs 30 j »** — le reporting sert un taux **par
 *     scénario**, jamais un global. Le calculer ici produirait un chiffre que le
 *     reporting réglementaire ne publie pas ; le taux servi, lui, est au panneau
 *     de l'alerte ouverte, pour le scénario qui l'a levée ;
 *  7. **les couleurs du score et du statut** — le dessin peint `92` en rouge,
 *     `64` en orange, `OUVERTE` en rouge. Aucune route ne sert de bandes de
 *     gravité ni de jugement d'état : les inventer ferait porter à l'écran un
 *     verdict que le serveur ne porte pas. C'est la conduite déjà tenue par
 *     `QueueShell`, et la mutation `backoffice-revue-file-etiquette-qui-juge`
 *     la garde ;
 *  8. **la pastille « Escaladées »** — l'alerte a trois états, et trois
 *     seulement : `OUVERTE`, `AFFECTEE`, `TRAITEE`. Aucune escalade n'existe en
 *     base. Les pastilles rendent donc le choix **servi** — « Toutes » et les
 *     trois états —, exactement comme le journal d'audit rend en pastilles ses
 *     deux lectures plutôt que la classification que personne ne sert ;
 *  9. **les trois commandes de décision** — « Classer sans suite », « Escalader
 *     au responsable », « Déclaration de soupçon ». Elles sont servies, par
 *     `AML_ALERT_HANDLE` et `SAR_SUBMIT`, et elles ne sont **pas** offertes ici :
 *     le serveur exige une **note** conservée avec la décision, et le dossier de
 *     soupçon passe en double validation. Un bouton qui déciderait sans
 *     recueillir la note écrirait une décision vide et irréversible — le dessin
 *     l'écrit lui-même : « la décision, son motif et son auteur sont figés dans
 *     le dossier ; une DS ne peut plus être retirée ». C'est la conduite déjà
 *     tenue sur le gel de compte du module Clients : l'écran qui recueille
 *     l'instruction est un chantier, pas un bouton.
 *
 * ## Le vocabulaire emprunté à la coquille
 *
 * `QueueShell` porte le parcours au clavier, le panneau et la région vivante ;
 * ses libellés parlent de « dossier », parce qu'elle a été écrite pour la revue
 * KYC. Ils sont conservés tels quels, pour la raison déjà écrite sur les cinq
 * autres appelants : réécrire les chaînes de la coquille par écran en ferait des
 * chaînes par écran, et c'est la coquille qui garantit que les files se
 * traversent toutes pareil.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueDetailSection, QueueFact, QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueDecision, QueueEntry } from '../shell/QueueShell.js';
import { useDeclareExport } from './export-context.js';
import { remettreLeFichier, toCsv } from './export-csv.js';
import { ApiError } from '../http/api-client.js';
import { useApiClient } from '../http/api-context.js';
import {
  fetchAlertQueue,
  fetchInvestigation,
  fetchMonitoringReport,
  fetchMonitoringRules,
  fetchReplayRuns,
  fetchSuspicionExport,
  fetchSuspicionFile,
} from '../http/surveillance.js';
import type {
  AlertQueueFilter,
  Investigation,
  MonitoringAlert,
  MonitoringReport,
  MonitoringRule,
  ReplayRun,
  ReportLine,
  SuspicionFile,
} from '../http/surveillance.js';

/**
 * Les six colonnes, dans l'ordre de la maquette — et **l'emphase que le dessin
 * donne à chacune**.
 *
 * Le dessin bâtit ses cellules avec quatre fabricants : `TAG` pour le score et
 * le statut, `T` pour le scénario, `NUM` pour le compte et le montant, `MUT`
 * pour l'âge. Les deux pastilles ne sont pas reprises — elles peignent un
 * jugement que le serveur ne porte pas —, mais tout le reste l'est : le compte
 * et les valeurs observées en chiffres tabulaires, l'instant de levée atténué.
 *
 * Deux colonnes changent de nom, et chacune parce que la route ne sert pas ce
 * que le dessin y écrit. « Montant » devient « Observé » : il n'existe pas de
 * montant d'alerte — il existe ce que le scénario a constaté, avec ses propres
 * clés, et élire « le montant » scénario par scénario serait la liste blanche
 * partagée de `G3`. « Âge » devient « Levée le » : rien ne mesure l'âge, et
 * l'horloge du poste n'est pas une mesure.
 */
export const alertColumns: readonly QueueColumn[] = [
  { id: 'severity', labelKey: 'compliance.columns.severity', emphasis: 'numeric' },
  { id: 'scenario', labelKey: 'compliance.columns.scenario', emphasis: 'plain' },
  { id: 'customer', labelKey: 'compliance.columns.customer', emphasis: 'numeric' },
  { id: 'observed', labelKey: 'compliance.columns.observed', emphasis: 'numeric' },
  { id: 'raisedAt', labelKey: 'compliance.columns.raisedAt', emphasis: 'muted' },
  { id: 'status', labelKey: 'compliance.columns.status', emphasis: 'plain' },
];

/**
 * Les trois états d'une alerte, **dans l'ordre où l'énumération les déclare**.
 *
 * C'est un miroir de `AlertStatus.java`, avec tout ce qu'un miroir coûte : le
 * jour où un quatrième état apparaîtrait en base, ce tableau deviendrait faux en
 * silence. `compliance-maquette.test.tsx` relit donc l'énumération Java et
 * confronte les valeurs, dans l'ordre — la conduite déjà tenue pour les types
 * d'entité du journal d'audit.
 *
 * Aucune route ne sert cette liste, et aucune n'est ouverte pour elle : trois
 * constantes n'ont pas besoin d'un aller-retour, et le serveur les nomme
 * lui-même dans son message de refus.
 */
export const alertStatuses: readonly string[] = ['OUVERTE', 'AFFECTEE', 'TRAITEE'];

/**
 * Les bornes de la gravité, telles que `SurveillanceApi` les refuse.
 *
 * Le contrôle est fait **ici aussi**, et ce n'est pas une défiance envers le
 * serveur : sans lui, une faute de frappe partirait en appel, reviendrait en
 * `400`, et l'écran n'aurait à afficher qu'un message générique de panne — le
 * client HTTP ne rend pas le texte du refus. Le miroir est gardé par un cas qui
 * relit les bornes dans le fichier Java.
 */
export const severityBounds = { min: 0, max: 100 } as const;

/** Les huit premiers caractères d'un identifiant — le premier groupe d'un `UUID`. */
const LONGUEUR_COURTE = 8;

/** La valeur que le serveur écrit quand une ligne d'historique n'a pas d'acteur. */
const ACTEUR_ABSENT = 'null';

/** Une lecture : sa valeur, ou la clé du message de son échec. */
interface Lecture<T> {
  readonly valeur: T | null;
  readonly erreur: string | null;
}

const enAttente = { valeur: null, erreur: null };

/** La clé de traduction de l'échec [echec]. */
function cleDEchec(echec: unknown): string {
  return echec instanceof ApiError ? echec.messageKey : 'errors.server';
}

/** L'écran : la file priorisée, et l'investigation de la ligne ouverte. */
export function ComplianceScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  const [filtre, setFiltre] = useState<AlertQueueFilter>({});
  const [file, setFile] = useState<Lecture<readonly MonitoringAlert[]>>(enAttente);
  const [regles, setRegles] = useState<Lecture<readonly MonitoringRule[]>>(enAttente);
  const [rapport, setRapport] = useState<Lecture<MonitoringReport>>(enAttente);
  const [avis, setAvis] = useState<string | null>(null);

  /**
   * Les analystes offerts au filtre « Affectée à ».
   *
   * Ils viennent de la **première** lecture, celle qu'aucun filtre ne réduit :
   * les prendre de la lecture courante les ferait disparaître au moment même où
   * l'on filtre dessus, et l'opérateur ne pourrait plus revenir en arrière.
   */
  const [analystes, setAnalystes] = useState<readonly string[]>([]);

  const lireLaFile = useCallback(
    async (demande: AlertQueueFilter, premiere: boolean) => {
      try {
        const lues = await fetchAlertQueue(client, demande);
        setFile({ valeur: lues, erreur: null });
        if (premiere) {
          setAnalystes([
            ...new Set(
              lues
                .map((alerte) => alerte.assignedTo)
                .filter((analyste): analyste is string => analyste !== null),
            ),
          ]);
        }
      } catch (echec) {
        // La file lue est effacée avec l'échec. La garder ferait lire les
        // alertes du filtre PRÉCÉDENT sous les pastilles du nouveau — sur une
        // file d'alertes, c'est la pire confusion possible.
        setFile({ valeur: null, erreur: cleDEchec(echec) });
      }
    },
    [client],
  );

  useEffect(() => {
    void lireLaFile({}, true);
  }, [lireLaFile]);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const lues = await fetchMonitoringRules(client);
        if (vivant) setRegles({ valeur: lues, erreur: null });
      } catch (echec) {
        if (vivant) setRegles({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    void (async () => {
      try {
        const lu = await fetchMonitoringReport(client);
        if (vivant) setRapport({ valeur: lu, erreur: null });
      } catch (echec) {
        // Un refus sur le reporting n'efface pas la file : l'habilitation porte
        // sur l'ACTION, et `AML_REPORTING_VIEW` n'est pas `AML_ALERT_QUEUE_VIEW`.
        if (vivant) setRapport({ valeur: null, erreur: cleDEchec(echec) });
      }
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  /** Applique [demande] à la file, pastilles comprises. */
  const appliquer = useCallback(
    (demande: AlertQueueFilter) => {
      setFiltre(demande);
      setFile(enAttente);
      void lireLaFile(demande, false);
    },
    [lireLaFile],
  );

  const instant = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(iso)),
    [i18n.language],
  );

  /** Une journée servie, mise en forme pour l'œil. La valeur ISO ne repart pas. */
  const journee = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(new Date(iso)),
    [i18n.language],
  );

  /**
   * Ce que le scénario a constaté, en une cellule.
   *
   * Les clés sont celles du serveur — `montant`, `seuil.amount_minor`,
   * `operations.serrees` —, dans l'ordre où il les rend. Aucune n'est traduite
   * ni élue : l'écran ne connaît pas les quatorze scénarios, et le premier qu'il
   * connaîtrait mal masquerait ce qu'il ne saurait pas nommer.
   */
  const observe = useCallback(
    (alerte: MonitoringAlert): string =>
      Object.entries(alerte.observed)
        .map(([cle, valeur]) => t('compliance.observedPair', { key: cle, value: valeur }))
        .join(t('compliance.observedSeparator')),
    [t],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (file.valeur ?? []).map((alerte) => ({
        id: alerte.id,
        reference: alerte.id.slice(0, LONGUEUR_COURTE),
        values: [
          String(alerte.severity),
          alerte.scenarioLabel,
          alerte.customerId.slice(0, LONGUEUR_COURTE),
          observe(alerte),
          instant(alerte.raisedAt),
          alerte.status,
        ],
        // Ce que le panneau montre à la place : la colonne tronque par sa
        // largeur, le panneau ne tronque pas. L'identifiant du client y est
        // entier — c'est celui qu'un analyste recopie dans un signalement — et
        // l'instant de levée porte la valeur exacte que le serveur a rendue.
        fullValues: [
          undefined,
          undefined,
          alerte.customerId,
          undefined,
          <time dateTime={alerte.raisedAt} key="raisedAt">
            {instant(alerte.raisedAt)}
          </time>,
        ],
      })),
    [file.valeur, instant, observe],
  );

  /**
   * Les trois filtres du dessin **que la route sert**, et leurs listes.
   *
   * « Période » n'y est pas : la route n'a pas de borne de temps. « Affectée à »
   * n'offre pas « Moi » : la session ne porte pas l'identifiant de l'opérateur.
   * Les deux manques sont dits sous la rangée, à l'endroit où la revue suivante
   * irait les redessiner.
   */
  const champsDuFiltre: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'scenario',
        labelKey: 'compliance.filters.scenario',
        control: 'select',
        placeholderKey: 'compliance.filters.allScenarios',
        options: [
          { value: '', label: t('compliance.filters.allScenarios') },
          // Les scénarios offerts sont ceux que des RÈGLES portent : un
          // scénario sans règle ne peut rien avoir levé, et l'offrir donnerait
          // un choix qui ne retiendrait jamais rien.
          ...[...new Map((regles.valeur ?? []).map((regle) => [regle.scenarioCode, regle])).values()].map(
            (regle) => ({
              value: regle.scenarioCode,
              label: t('compliance.filters.scenarioOption', {
                code: regle.scenarioCode,
                label: regle.label,
              }),
            }),
          ),
        ],
      },
      { name: 'minimumSeverity', labelKey: 'compliance.filters.minimumSeverity' },
      {
        name: 'assignedTo',
        labelKey: 'compliance.filters.assignedTo',
        control: 'select',
        placeholderKey: 'compliance.filters.allAnalysts',
        options: [
          { value: '', label: t('compliance.filters.allAnalysts') },
          ...analystes.map((analyste) => ({
            // La VALEUR est l'identifiant entier — c'est elle qui part vers la
            // route ; le LIBELLÉ est la forme courte, celle de la colonne.
            value: analyste,
            label: analyste.slice(0, LONGUEUR_COURTE),
          })),
        ],
      },
    ],
    [analystes, regles.valeur, t],
  );

  const valeursDuFiltre = useMemo(
    () => ({
      scenario: filtre.scenario ?? '',
      minimumSeverity: filtre.minimumSeverity ?? '',
      assignedTo: filtre.assignedTo ?? '',
    }),
    [filtre],
  );

  /**
   * Filtre, **ou refuse une gravité hors bornes sans appeler**.
   *
   * Le serveur refuse déjà `0..100`, et son message le dit ; le client HTTP, lui,
   * ne rend que la nature de l'échec. Sans ce contrôle, une faute de frappe
   * s'afficherait « le serveur a refusé la requête » et l'opérateur chercherait
   * une panne.
   */
  const filtrer = useCallback(
    (valeurs: Record<string, string>) => {
      const gravite = (valeurs.minimumSeverity ?? '').trim();
      if (gravite !== '') {
        const nombre = Number(gravite);
        if (
          !Number.isInteger(nombre) ||
          nombre < severityBounds.min ||
          nombre > severityBounds.max
        ) {
          setAvis(
            t('compliance.filters.severityOutOfBounds', {
              min: severityBounds.min,
              max: severityBounds.max,
            }),
          );
          return;
        }
      }
      setAvis(null);
      appliquer({
        ...filtre,
        scenario: valeurs.scenario,
        minimumSeverity: gravite,
        assignedTo: valeurs.assignedTo,
      });
    },
    [appliquer, filtre, t],
  );

  // --- L'investigation de la ligne ouverte ---------------------------------

  const [ouverte, setOuverte] = useState<MonitoringAlert | null>(null);
  const [dossier, setDossier] = useState<Lecture<Investigation>>(enAttente);
  const [rejeux, setRejeux] = useState<Lecture<readonly ReplayRun[]>>(enAttente);
  const [soupcon, setSoupcon] = useState<Lecture<SuspicionFile>>(enAttente);

  /**
   * Ouvre une alerte — **et c'est cet appel qui tient la promesse du dessin**.
   *
   * Ce qui s'affiche est ce que le serveur vient de rendre, jamais la ligne déjà
   * reçue : une fiche client montrée sans que le journal la nomme serait la
   * promesse affichée et non tenue. Le rejeu de la règle part du même geste —
   * il ne révèle aucune donnée personnelle, et il répond à la seule question que
   * l'analyste se pose devant une alerte douteuse : « combien ce scénario en
   * aurait-il produit ? » (`EX-AML-010`).
   *
   * Le dossier de soupçon, lui, **ne part pas d'office** : le consulter est une
   * consultation de plus, et elle doit être demandée.
   */
  const ouvrir = useCallback(
    (ligne: QueueEntry) => {
      const alerte = (file.valeur ?? []).find((candidate) => candidate.id === ligne.id) ?? null;
      setOuverte(alerte);
      setDossier(enAttente);
      setRejeux(enAttente);
      setSoupcon(enAttente);
      if (alerte === null) return;
      void (async () => {
        try {
          setDossier({ valeur: await fetchInvestigation(client, alerte.id), erreur: null });
        } catch (echec) {
          setDossier({ valeur: null, erreur: cleDEchec(echec) });
        }
      })();
      void (async () => {
        try {
          setRejeux({ valeur: await fetchReplayRuns(client, alerte.ruleCode), erreur: null });
        } catch (echec) {
          setRejeux({ valeur: null, erreur: cleDEchec(echec) });
        }
      })();
    },
    [client, file.valeur],
  );

  /**
   * Les commandes du panneau — **et il n'y en a aucune quand rien ne les
   * justifie**.
   *
   * Les deux ne s'offrent que sur une alerte qui porte un dossier de soupçon.
   * Une commande qui répondrait « il n'y a pas de dossier » serait un bouton
   * inerte, et un bouton inerte apprend à ne plus cliquer.
   */
  const commandes: readonly QueueDecision[] = useMemo(
    () =>
      (dossier.valeur?.suspicionFileId ?? null) === null
        ? []
        : [
            {
              id: 'suspicion',
              labelKey: 'compliance.actions.openSuspicion',
              shortcut: 'd',
              advances: false,
            },
            {
              id: 'export',
              labelKey: 'compliance.actions.exportSuspicion',
              shortcut: 'e',
              advances: false,
            },
          ],
    [dossier.valeur],
  );

  const commander = useCallback(
    (_ligne: QueueEntry, commande: string) => {
      const identifiant = dossier.valeur?.suspicionFileId ?? null;
      if (identifiant === null) return;
      void (async () => {
        if (commande === 'suspicion') {
          try {
            setSoupcon({ valeur: await fetchSuspicionFile(client, identifiant), erreur: null });
          } catch (echec) {
            setSoupcon({ valeur: null, erreur: cleDEchec(echec) });
          }
          return;
        }
        try {
          const texte = await fetchSuspicionExport(client, identifiant);
          const nom = t('compliance.actions.suspicionFileName', { id: identifiant });
          setAvis(
            remettreLeFichier(nom, texte, 'text/plain;charset=utf-8')
              ? t('compliance.actions.exported', { name: nom })
              : t('compliance.actions.exportImpossible'),
          );
        } catch (echec) {
          setAvis(t(cleDEchec(echec)));
        }
      })();
    },
    [client, dossier.valeur, t],
  );

  // --- L'export de la barre supérieure -------------------------------------

  const enTetesDuFichier = useMemo(
    () => alertColumns.map((colonne) => t(colonne.labelKey)),
    [t],
  );

  /**
   * Ce que le bouton « Exporter » du dessin écrit : **les lignes déjà reçues**.
   *
   * Rien n'est inventé et rien n'est redemandé au serveur — c'est la réponse en
   * main, recopiée. Sans ligne, aucune déclaration : la barre ne dessine alors
   * pas de bouton, plutôt que d'en dessiner un qui écrirait un fichier vide sous
   * un nom qui promet des alertes.
   */
  const exportDeLaBarre = useMemo(() => {
    if (lignes.length === 0) return null;
    return () => {
      const nom = t('compliance.exportName');
      setAvis(
        remettreLeFichier(
          nom,
          toCsv(enTetesDuFichier, lignes.map((ligne) => ligne.values)),
        )
          ? t('compliance.actions.exported', { name: nom })
          : t('compliance.actions.exportImpossible'),
      );
    };
  }, [enTetesDuFichier, lignes, t]);
  useDeclareExport(exportDeLaBarre);

  // --- Le rendu ------------------------------------------------------------

  const reglesParCode = useMemo(
    () => new Map((regles.valeur ?? []).map((regle) => [regle.code, regle])),
    [regles.valeur],
  );

  const lignesDuRapport = useMemo(
    () => new Map((rapport.valeur?.lines ?? []).map((ligne) => [ligne.scenarioCode, ligne])),
    [rapport.valeur],
  );

  return (
    <section className="compliance" aria-label={t('compliance.landmark')}>
      {avis === null ? null : (
        <p className="compliance__outcome" role="status" data-testid="compliance-outcome">
          {avis}
        </p>
      )}

      {/* La rangée de filtres du dessin : les contrôles et la commande sur UNE
          ligne, en tête de la carte, et non trois blocs empilés. */}
      <FormShell
        fields={champsDuFiltre}
        layout="inline"
        values={valeursDuFiltre}
        onSubmit={filtrer}
        submitLabelKey="compliance.filters.submit"
      />

      {/* Les pastilles du dessin, et elles portent le seul choix que le serveur
          sert : l'état de l'alerte. « Escaladées » n'existe pas en base. */}
      <div className="compliance__chips" role="group" aria-label={t('compliance.chips.landmark')}>
        <button
          type="button"
          className="compliance__chip"
          aria-pressed={filtre.status === undefined || filtre.status === ''}
          onClick={() => {
            appliquer({ ...filtre, status: '' });
          }}
        >
          {t('compliance.chips.all')}
        </button>
        {alertStatuses.map((statut) => (
          <button
            key={statut}
            type="button"
            className="compliance__chip"
            data-status={statut}
            aria-pressed={filtre.status === statut}
            onClick={() => {
              appliquer({ ...filtre, status: statut });
            }}
          >
            {t(`compliance.chips.${statut}`)}
          </button>
        ))}
      </div>

      <ComplianceStats
        alerts={file.valeur}
        report={rapport.valeur}
        reportError={rapport.erreur}
        formatDay={journee}
      />

      {file.erreur === null ? null : (
        <p className="compliance__notice" role="alert">
          {t(file.erreur)}
        </p>
      )}

      {file.valeur === null ? (
        file.erreur === null ? (
          <p className="compliance__idle">{t('compliance.loading')}</p>
        ) : null
      ) : (
        <QueueShell
          columns={alertColumns}
          entries={lignes}
          onOpen={ouvrir}
          decisions={commandes}
          onDecide={commander}
          renderDetail={() => (
            <AlertDetail
              alert={ouverte}
              investigation={dossier}
              replays={rejeux}
              suspicion={soupcon}
              rule={ouverte === null ? undefined : reglesParCode.get(ouverte.ruleCode)}
              reportLine={
                ouverte === null ? undefined : lignesDuRapport.get(ouverte.scenarioCode)
              }
              report={rapport.valeur}
              formatInstant={instant}
              formatDay={journee}
            />
          )}
          /* **Le pied du dessin, DANS la carte.** À gauche le compte ; aucune
             pagination à droite, parce que la route n'en a pas : elle rend une
             page unique, bornée côté serveur. Une rangée de rangs de page y
             serait un contrôle qui ne commande rien. */
          footer={
            <span className="compliance__count" data-testid="compliance-count">
              {t('compliance.count', { count: lignes.length })}
            </span>
          }
        />
      )}

      {/* **Ce que le serveur ne sert pas, déclaré — mais pas en travers de
          l'écran.** Quatre lignes tenaient le haut de la page, et le dessin n'a
          aucun bloc en tête : le porteur l'a fait retirer du journal d'audit
          pour cette raison exacte, puis de la finance. La déclaration reste
          entière, au mot près ; elle change de forme et de place. Fermée, elle
          ne coûte rien à la page ; ouverte, elle dit ce qu'elle disait. Elle
          n'est ni masquée ni conditionnelle : le texte est dans la page, un
          lecteur d'écran le traverse, et un contrôle le trouve.

          Ce qui NE descend pas ici, c'est ce qui doit se lire à côté du chiffre
          qu'il qualifie : la phrase de chaque indicateur non mesuré reste sous
          son tiret, et la prohibition d'`EX-AML-008` reste dans le panneau. */}
      <details className="compliance__omissions">
        <summary className="compliance__omissions-summary">
          {t('compliance.scopeSummary')}
        </summary>
        <p className="compliance__omissions-body">{t('compliance.scopeNotice')}</p>
      </details>
    </section>
  );
}

/**
 * Les quatre indicateurs du dessin — **deux mesurés, deux pas**.
 *
 * C'est la conduite du tableau de bord, et c'est ici qu'elle compte le plus :
 * sur l'écran de la conformité, un « 0 » là où rien ne mesure se lit comme une
 * bonne nouvelle. Les deux que rien ne mesure portent donc, à la place d'un
 * compte, le **tiret de l'absence de mesure** et la phrase qui le dit — sans
 * couleur d'état, et sans lien.
 *
 * L'en-tête porte en plus le compte de ce qui est mesuré — « 2 indicateurs
 * mesurés sur 4 » —, **dérivé** des indicateurs eux-mêmes : un cinquième qui
 * entrerait sans route le ferait bouger tout seul.
 */
function ComplianceStats({
  alerts,
  report,
  reportError,
  formatDay,
}: {
  readonly alerts: readonly MonitoringAlert[] | null;
  readonly report: MonitoringReport | null;
  readonly reportError: string | null;
  readonly formatDay: (iso: string) => string;
}): ReactNode {
  const { t } = useTranslation();

  const fenetre =
    report === null
      ? null
      : t('compliance.stats.window', { from: formatDay(report.from), to: formatDay(report.to) });

  const indicateurs = [
    {
      id: 'queued',
      label: t('compliance.stats.queued'),
      valeur: alerts === null ? null : String(alerts.length),
      meta: t('compliance.stats.queuedMeta'),
    },
    {
      // Chantier 11. Rien ne mesure l'âge d'une alerte : cet indicateur ne
      // porte AUCUN chiffre. Voir l'en-tête du fichier.
      id: 'overdue',
      label: t('compliance.stats.overdue'),
      valeur: null,
      meta: t('compliance.stats.overdueMeta'),
    },
    {
      // Le reporting sert un taux PAR SCÉNARIO ; aucun global. Le taux servi
      // est au panneau, pour le scénario de l'alerte ouverte.
      id: 'falsePositives',
      label: t('compliance.stats.falsePositives'),
      valeur: null,
      meta: t('compliance.stats.falsePositivesMeta'),
    },
    {
      id: 'suspicions',
      label: t('compliance.stats.suspicions'),
      valeur: report === null ? null : String(report.suspicionFiles),
      meta:
        reportError !== null
          ? t(reportError)
          : (fenetre ?? t('compliance.stats.reportLoading')),
    },
  ];

  const mesures = indicateurs.filter((indicateur) => indicateur.valeur !== null).length;

  return (
    <section className="compliance__stats" aria-label={t('compliance.stats.landmark')}>
      <p className="compliance__stats-measured" data-testid="compliance-measured">
        {t('compliance.stats.measured', { measured: mesures, total: indicateurs.length })}
      </p>
      <ul className="compliance__stat-list">
        {indicateurs.map((indicateur) => (
          <li className="compliance__stat" key={indicateur.id}>
            <span className="compliance__stat-label">{indicateur.label}</span>
            <span
              className="compliance__stat-value"
              data-measured={indicateur.valeur !== null}
              data-testid={`compliance-stat-${indicateur.id}`}
            >
              {indicateur.valeur ?? t('compliance.stats.notMeasuredValue')}
            </span>
            <span className="compliance__stat-meta">{indicateur.meta}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Le panneau d'une alerte ouverte : ce que les **autres** appels ont rendu.
 *
 * La première section — le Résumé — est bâtie par la coquille à partir des
 * colonnes, et cet écran n'a plus à l'écrire. Les suivantes suivent l'ordre du
 * dessin : le scénario déclenché, puis ce qu'on sait du compte.
 */
function AlertDetail({
  alert,
  investigation,
  replays,
  suspicion,
  rule,
  reportLine,
  report,
  formatInstant,
  formatDay,
}: {
  readonly alert: MonitoringAlert | null;
  readonly investigation: Lecture<Investigation>;
  readonly replays: Lecture<readonly ReplayRun[]>;
  readonly suspicion: Lecture<SuspicionFile>;
  readonly rule: MonitoringRule | undefined;
  readonly reportLine: ReportLine | undefined;
  readonly report: MonitoringReport | null;
  readonly formatInstant: (iso: string) => string;
  readonly formatDay: (iso: string) => string;
}): ReactNode {
  const { t } = useTranslation();
  if (alert === null) return <p className="compliance__idle">{t('compliance.alertMissing')}</p>;

  const enquete = investigation.valeur;

  return (
    <>
      {/* **Le scénario déclenché** — la première section du dessin. Ses trois
          lignes y sont : la règle, le seuil CONFIGURÉ, et ce qui a été observé.
          Aucun seuil n'est écrit ici : ils viennent de `parameters`, que le
          back-office règle sans développement. */}
      <QueueDetailSection title={t('compliance.detail.scenarioTitle')}>
        <QueueFact label={t('compliance.detail.rule')} testId="compliance-detail-rule">
          {t('compliance.detail.ruleValue', {
            code: alert.ruleCode,
            version: alert.ruleVersion,
          })}
        </QueueFact>
        {rule === undefined ? (
          <QueueFact label={t('compliance.detail.thresholds')} testId="compliance-detail-thresholds">
            {t('compliance.detail.ruleUnknown')}
          </QueueFact>
        ) : (
          <>
            <QueueFact label={t('compliance.detail.mode')} testId="compliance-detail-mode">
              {rule.mode}
            </QueueFact>
            <QueueFact label={t('compliance.detail.perimeter')}>{rule.perimeter}</QueueFact>
            {Object.entries(rule.parameters).map(([cle, valeur]) => (
              <QueueFact
                key={cle}
                label={t('compliance.detail.threshold', { key: cle })}
                testId={`compliance-detail-threshold-${cle}`}
              >
                {valeur}
              </QueueFact>
            ))}
          </>
        )}
        {Object.entries(alert.observed).map(([cle, valeur]) => (
          <QueueFact
            key={cle}
            label={t('compliance.detail.observed', { key: cle })}
            testId={`compliance-detail-observed-${cle}`}
          >
            {valeur}
          </QueueFact>
        ))}
      </QueueDetailSection>

      {/* **Le dispositif, mesuré par lui-même** (`EX-AML-009`) — pour CE
          scénario, sur la fenêtre que le serveur a appliquée. C'est le taux de
          faux positifs que le dessin met en indicateur global ; il est servi
          par scénario, et il est rendu là où il est servi. */}
      {reportLine === undefined || report === null ? null : (
        <QueueDetailSection
          title={t('compliance.detail.reportTitle', {
            from: formatDay(report.from),
            to: formatDay(report.to),
          })}
        >
          <QueueFact label={t('compliance.detail.raised')}>{String(reportLine.raised)}</QueueFact>
          <QueueFact label={t('compliance.detail.handled')}>{String(reportLine.handled)}</QueueFact>
          <QueueFact
            label={t('compliance.detail.falsePositives')}
            testId="compliance-detail-false-positives"
          >
            {t('compliance.detail.falsePositivesValue', {
              count: reportLine.falsePositives,
              percent: reportLine.falsePositivePercent,
            })}
          </QueueFact>
          <QueueFact label={t('compliance.detail.averageHandling')}>
            {t('compliance.detail.seconds', { count: reportLine.averageHandlingSeconds })}
          </QueueFact>
          <QueueFact
            label={t('compliance.detail.recalibration')}
            testId="compliance-detail-recalibration"
          >
            {t(
              reportLine.recalibrationCandidate
                ? 'compliance.detail.recalibrationYes'
                : 'compliance.detail.recalibrationNo',
            )}
          </QueueFact>
        </QueueDetailSection>
      )}

      {/* **Le rejeu** (`EX-AML-010`) : combien d'alertes cette règle AURAIT
          produites. Jamais levées, jamais mises en file — c'est ce qui permet
          d'estimer la charge d'un seuil avant de l'activer. */}
      <QueueDetailSection title={t('compliance.detail.replayTitle')}>
        {replays.erreur !== null ? (
          <QueueFact label={t('compliance.detail.replayLast')}>{t(replays.erreur)}</QueueFact>
        ) : replays.valeur === null ? (
          <QueueFact label={t('compliance.detail.replayLast')}>
            {t('compliance.detail.loading')}
          </QueueFact>
        ) : replays.valeur.length === 0 ? (
          <QueueFact label={t('compliance.detail.replayLast')} testId="compliance-detail-replay">
            {t('compliance.detail.replayNone')}
          </QueueFact>
        ) : (
          <>
            <QueueFact
              label={t('compliance.detail.replayWouldHaveRaised')}
              testId="compliance-detail-replay"
            >
              {String(replays.valeur[0].wouldHaveRaised)}
            </QueueFact>
            <QueueFact label={t('compliance.detail.replayScanned')}>
              {String(replays.valeur[0].observationsScanned)}
            </QueueFact>
            <QueueFact label={t('compliance.detail.replayPeriod')}>
              {t('compliance.detail.replayPeriodValue', {
                from: formatDay(replays.valeur[0].periodStart),
                to: formatDay(replays.valeur[0].periodEnd),
              })}
            </QueueFact>
          </>
        )}
      </QueueDetailSection>

      {/* **L'investigation en un clic** — et c'est cet appel-là que le journal
          d'audit nomme, avec le nombre de contreparties révélées. */}
      <QueueDetailSection title={t('compliance.detail.investigationTitle')}>
        {investigation.erreur !== null ? (
          <QueueFact label={t('compliance.detail.customerRef')}>
            {t(investigation.erreur)}
          </QueueFact>
        ) : enquete === null ? (
          <QueueFact label={t('compliance.detail.customerRef')}>
            {t('compliance.detail.investigating')}
          </QueueFact>
        ) : (
          <>
            <QueueFact
              label={t('compliance.detail.customerRef')}
              testId="compliance-detail-customer-ref"
            >
              {enquete.customerPublicRef ?? t('compliance.detail.customerRefMissing')}
            </QueueFact>
            <QueueFact label={t('compliance.detail.kycCase')} testId="compliance-detail-kyc">
              {enquete.kycCaseId ?? t('compliance.detail.kycCaseMissing')}
            </QueueFact>
            <QueueFact label={t('compliance.detail.balance')}>
              {enquete.balanceMinorUnits === null
                ? t('compliance.detail.balanceMissing')
                : String(enquete.balanceMinorUnits)}
            </QueueFact>
            <QueueFact
              label={t('compliance.detail.priorAlerts')}
              testId="compliance-detail-prior-alerts"
            >
              {String(enquete.priorAlerts.length)}
            </QueueFact>
            <QueueFact label={t('compliance.detail.operations')}>
              {String(enquete.history.length)}
            </QueueFact>
          </>
        )}
      </QueueDetailSection>

      {enquete === null ? null : (
        <>
          <CounterpartySection investigation={enquete} formatDay={formatDay} />

          {/* **L'historique de l'alerte** : ce qui lui est arrivé, et par qui.
              Une ligne sans acteur arrive du serveur comme les quatre lettres
              `null` — elle est nommée « le dispositif » plutôt qu'affichée
              telle quelle à un analyste qui la lirait comme une panne. */}
          <QueueDetailSection title={t('compliance.detail.timelineTitle')}>
            {enquete.timeline.length === 0 ? (
              <QueueFact label={t('compliance.detail.timelineEmpty')}>
                {t('compliance.detail.timelineEmptyValue')}
              </QueueFact>
            ) : (
              enquete.timeline.map((evenement, rang) => (
                <QueueFact
                  key={`${evenement.kind}-${evenement.at}-${String(rang)}`}
                  label={t('compliance.detail.timelineEvent', {
                    kind: evenement.kind,
                    instant: formatInstant(evenement.at),
                  })}
                >
                  {t('compliance.detail.timelineValue', {
                    detail: evenement.detail,
                    actor:
                      evenement.actorId === ACTEUR_ABSENT
                        ? t('compliance.detail.actorSystem')
                        : evenement.actorId.slice(0, LONGUEUR_COURTE),
                  })}
                </QueueFact>
              ))
            )}
          </QueueDetailSection>

          <QueueDetailSection title={t('compliance.detail.attachmentsTitle')}>
            {enquete.attachments.length === 0 ? (
              <QueueFact
                label={t('compliance.detail.attachmentsNone')}
                testId="compliance-detail-attachments"
              >
                {t('compliance.detail.attachmentsNoneValue')}
              </QueueFact>
            ) : (
              enquete.attachments.map((piece) => (
                <QueueFact key={piece.id} label={piece.filename}>
                  {t('compliance.detail.attachmentValue', {
                    type: piece.mediaType,
                    bytes: piece.byteSize,
                  })}
                </QueueFact>
              ))
            )}
          </QueueDetailSection>

          <SuspicionSection fileId={enquete.suspicionFileId} reading={suspicion} />
        </>
      )}

      {/* **La note du dessin, à l'endroit où le dessin la met** — sous les
          commandes du panneau, en petit et en gris. Elle est vraie : le serveur
          fige la décision, son motif et son auteur, et un dossier de soupçon ne
          se retire pas. */}
      <section className="queue__detail-section">
        <p className="compliance__note">{t('compliance.note')}</p>
      </section>
    </>
  );
}

/**
 * **Le graphe des contreparties, dans la langue du dessin** (`EX-AML-006`).
 *
 * Une ligne par arête : qui envoie à qui à gauche, le nombre d'opérations, le
 * total et la moyenne à droite. Voir l'en-tête du fichier pour la décision — le
 * porteur n'a dessiné aucun diagramme, et en inventer un ici aurait été inventer
 * une mise en page.
 *
 * **Un graphe vide n'est pas un graphe absent**, et les deux se disent
 * différemment : « aucune contrepartie sur la fenêtre » est une mesure — le
 * serveur a regardé quatre-vingt-dix jours et n'a rien trouvé —, et la fenêtre
 * regardée est nommée avec. Sans elle, un compte qui n'a reçu que des dépôts
 * d'espèces se lirait comme un compte dont on n'a pas su lire les liens.
 */
function CounterpartySection({
  investigation,
  formatDay,
}: {
  readonly investigation: Investigation;
  readonly formatDay: (iso: string) => string;
}): ReactNode {
  const { t } = useTranslation();
  const graphe = investigation.graph;
  const centre = graphe.centre;

  return (
    <QueueDetailSection
      title={t('compliance.detail.graphTitle', {
        from: formatDay(graphe.from),
        to: formatDay(graphe.to),
      })}
    >
      {graphe.edges.length === 0 ? (
        <QueueFact label={t('compliance.detail.graphEmpty')} testId="compliance-detail-graph">
          {t('compliance.detail.graphEmptyValue')}
        </QueueFact>
      ) : (
        graphe.edges.map((arete) => (
          <QueueFact
            key={`${arete.from}-${arete.to}-${arete.firstSeen}`}
            label={t(
              arete.from === centre
                ? 'compliance.detail.graphOutgoing'
                : 'compliance.detail.graphIncoming',
              {
                counterparty: (arete.from === centre ? arete.to : arete.from).slice(
                  0,
                  LONGUEUR_COURTE,
                ),
              },
            )}
            testId="compliance-detail-graph"
          >
            {t('compliance.detail.graphEdgeValue', {
              operations: arete.operations,
              total: arete.totalAmount,
              average: arete.averageAmount,
            })}
          </QueueFact>
        ))
      )}
    </QueueDetailSection>
  );
}

/**
 * **Le dossier de déclaration de soupçon — et la prohibition d'`EX-AML-008`.**
 *
 * Cette section ne porte **aucun contrôle**. Pas de bouton « informer le
 * client », pas de case « prévenir », pas de champ de message : le système ne
 * génère aucune notification client sur ce chemin, par aucun canal, et
 * l'interdiction prime sur toute règle de notification — y compris la
 * notification écrite inconditionnelle d'`EX-NOT-001`. Un écran qui offrirait
 * l'affordance défferait une règle que le serveur tient.
 *
 * Elle le **dit** aussi, et pas seulement pour l'analyste : une section qui se
 * contenterait de ne rien offrir se verrait compléter à la première revue par
 * quelqu'un qui la croirait inachevée.
 */
function SuspicionSection({
  fileId,
  reading,
}: {
  readonly fileId: string | null;
  readonly reading: Lecture<SuspicionFile>;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <>
    <QueueDetailSection title={t('compliance.detail.suspicionTitle')}>
      {fileId === null ? (
        <QueueFact
          label={t('compliance.detail.suspicionNone')}
          testId="compliance-detail-suspicion"
        >
          {t('compliance.detail.suspicionNoneValue')}
        </QueueFact>
      ) : reading.erreur !== null ? (
        <QueueFact label={t('compliance.detail.suspicionReference')}>
          {t(reading.erreur)}
        </QueueFact>
      ) : reading.valeur === null ? (
        <QueueFact
          label={t('compliance.detail.suspicionReference')}
          testId="compliance-detail-suspicion"
        >
          {t('compliance.detail.suspicionClosed')}
        </QueueFact>
      ) : (
        <>
          <QueueFact
            label={t('compliance.detail.suspicionReference')}
            testId="compliance-detail-suspicion"
          >
            {reading.valeur.reference}
          </QueueFact>
          <QueueFact label={t('compliance.detail.suspicionStatus')}>
            {reading.valeur.status}
          </QueueFact>
          <QueueFact label={t('compliance.detail.suspicionSummary')}>
            {reading.valeur.summary}
          </QueueFact>
          <QueueFact label={t('compliance.detail.suspicionAuthority')}>
            {reading.valeur.authorityReference ?? t('compliance.detail.suspicionNotSubmitted')}
          </QueueFact>
          {reading.valeur.items.map((piece) => (
            <QueueFact
              key={`${String(piece.position)}-${piece.reference}`}
              label={t('compliance.detail.suspicionItem', {
                position: piece.position,
                kind: piece.kind,
              })}
            >
              {piece.label}
            </QueueFact>
          ))}
        </>
      )}
    </QueueDetailSection>
    {/* La prohibition est écrite HORS de la liste de faits : un `<p>` dans un
        `<dl>` n'est pas du balisage valide, et ce n'est pas un fait du dossier
        — c'est ce que le produit s'interdit de faire avec. */}
    <p className="compliance__prohibition" data-testid="compliance-prohibition">
      {t('compliance.detail.noNotification')}
    </p>
    </>
  );
}
